// @givmo/step-up — recognising the backend's step-up refusal (AS-WEB3; lifted at CCM-R1-STEP-UP so
// every module's gated act shares it: payout's, and Compliance's signing acts in the next cut).
//
// Moved verbatim from `apps/charity/src/features/payment/payment-step-up.ts`; the words below were
// written for the payout mutations and hold for any gated act.
//
// AS-BE9 gates the payout mutations (`submit_ach`, `elect_check`) on how recently the session proved
// its identity. When that proof is stale the gated endpoint answers a typed **HTTP 400** on the
// action's NORMAL error channel — deliberately not a 401/403, which the app would read as "signed
// out" and which the api-client's own reauth-replay rail (limited-use App Check, `reauth_required`)
// would try to handle. This is a different rail: an ordinary application error carrying a machine
// code the caller is expected to branch on.
//
//   {"errors":[{"code":400,"title":"Verification required","detail":"…","request_id":"…",
//     "error_code":"step_up_required","context":{"action":"charity_payout_ach"}}]}
//
// `context.action` is the gate's scope (`@require_recent_auth("charity_payout_ach")` on
// `submit_ach`), never the operation's own name; it is what a copy table's rows are keyed by.
//
// ⚠ INERT UNTIL THE BACKEND FLAG FLIPS. `STEP_UP_AUTH_ENABLED` is off in every deployed environment
// today, so this code path is never entered — the mutations simply succeed. Nothing here has a
// client-side flag of its own: adding one would mean two switches to align for a single behaviour.
//
// The detector reads the machine `error_code`, never the English `detail` — the same PII-safe branch
// key iOS uses, and the only part of the envelope that is contractually stable.

import {
  firstErrorCode,
  firstErrorContext,
  isGivmoError,
  type GivmoError,
} from '@givmo/api-client';
import { STEP_UP_WAYS_IN, StepUpPasskeyFailure, type StepUpWayIn } from './step-up-host';

/** The backend's machine-readable refusal code for a stale identity proof. */
export const STEP_UP_REQUIRED_ERROR_CODE = 'step_up_required';

/**
 * The backend's refusal for a factor change only a passkey confirmation clears (SEC2-AS rules 1
 * and 2: adding a passkey when the account holds one, removing the last). Same shape as
 * `step_up_required`, its own code, because its cure is narrower. A session held to the emailed
 * code's limits on an account that holds a passkey meets it too (AS-PWL-2; at Compliance's
 * stricter levels since AS-PWL-2e), with `context.confirm_with: "passkey"`.
 */
export const PASSKEY_CONFIRMATION_REQUIRED_ERROR_CODE = 'passkey_confirmation_required';

/**
 * AS-PWL-2 / AS-PWL-2e — the backend's refusal of a session held to the emailed code's limits on
 * an account that holds NO passkey: the same shape, `context.confirm_with` naming the cure
 * (`"reauth"`, or `"add_passkey"`). The gate answers it as it answers `step_up_required`, with
 * every way in: a passkey-less account carries no restriction, so any fresh re-sign-in the person
 * holds here passes, and where they hold none the passkey-only dead end fails the act with
 * `StepUpPasskeyFailure('none_registered')`, which a caller turns into the offer to set one up.
 */
export const PASSKEY_SETUP_REQUIRED_ERROR_CODE = 'passkey_setup_required';

/**
 * What a module's resolver says about one refusal it named: the row whose words the prompt shows,
 * and the ways in that refusal can be answered with. A returned `accepts` INTERSECTS the prompt's own
 * (it never widens them); an empty intersection is the honest "no way to confirm here" state. A bare
 * string is the row alone; `undefined` names neither.
 */
export type StepUpRefusalRow =
  | string
  | { readonly row?: string; readonly accepts?: readonly StepUpWayIn[] }
  | undefined;

/** A module's reading of one refusal, from the error itself (its `context`, say). */
export type StepUpRefusalResolver = (error: GivmoError) => StepUpRefusalRow;

/**
 * The refusal codes a gate answers with a prompt, beyond the package's own two. A list answers each
 * code with the refusal's own `context.action` row; a record also names the row per code — a fixed
 * row key, or a resolver — because a module's own refusal may carry no `context.action` at all
 * (Board's signing refusal carries none). An unlisted code passes through as the act's own error.
 */
export type StepUpRefusalCodes =
  | readonly string[]
  | Readonly<Record<string, string | StepUpRefusalResolver>>;

/** How the gate answers a refusal: the ways its code allows, and the row its code names, if any. */
export interface StepUpRefusalAnswer {
  readonly ways: readonly StepUpWayIn[];
  readonly row: string | null;
}

function isRefusalList(codes: StepUpRefusalCodes): codes is readonly string[] {
  return Array.isArray(codes);
}

/**
 * The gate's answer to a refusal, or `null` when it does not answer it with a prompt.
 *
 * `step_up_required` and `passkey_setup_required` allow every way (the prompt narrows them);
 * `passkey_confirmation_required` allows only the passkey, whatever the prompt says. A code the
 * module names allows every way, its
 * resolver's `accepts` intersecting them; a module may also name a row for the package's own two
 * codes, and may only narrow their ways. The record is read by OWN key (`Object.hasOwn`), so a code
 * like `constructor` is never answered by the object's prototype.
 */
export function stepUpRefusalAnswer(
  error: unknown,
  refusalCodes: StepUpRefusalCodes = [],
): StepUpRefusalAnswer | null {
  if (!isGivmoError(error)) return null;
  const code = firstErrorCode(error);
  if (code === undefined || code === null) return null;

  const named = isRefusalList(refusalCodes)
    ? refusalCodes.includes(code)
    : Object.hasOwn(refusalCodes, code);
  let ways: readonly StepUpWayIn[];
  if (code === PASSKEY_CONFIRMATION_REQUIRED_ERROR_CODE) ways = ['passkey'];
  else if (
    code === STEP_UP_REQUIRED_ERROR_CODE ||
    code === PASSKEY_SETUP_REQUIRED_ERROR_CODE ||
    named
  )
    ways = STEP_UP_WAYS_IN;
  else return null;

  if (isRefusalList(refusalCodes) || !named) return { ways, row: null };
  const entry = refusalCodes[code];
  const read: StepUpRefusalRow = typeof entry === 'function' ? entry(error) : entry;
  if (read === undefined) return { ways, row: null };
  if (typeof read === 'string') return { ways, row: read };
  const narrowed =
    read.accepts === undefined ? ways : ways.filter((way) => read.accepts?.includes(way));
  return { ways: narrowed, row: read.row ?? null };
}

/**
 * A refusal the CLIENT raises before any server could (AS-WEB14): Identity Platform's own
 * recent-sign-in rule on an authenticator change, or a session whose first factor cannot make it.
 * `null` for anything else. What it returns reads like a named code's resolver: the row whose words
 * the prompt shows, and the ways in it can be answered with, which only ever narrow every way in.
 */
export type StepUpClientRefusal = (
  error: unknown,
) => { readonly row?: string; readonly accepts?: readonly StepUpWayIn[] } | null;

/** The gate's answer to a client refusal, or `null` when the error is not one. */
export function stepUpClientRefusalAnswer(
  error: unknown,
  clientRefusal?: StepUpClientRefusal,
): StepUpRefusalAnswer | null {
  if (clientRefusal === undefined) return null;
  const read = clientRefusal(error);
  if (read === null) return null;
  const ways =
    read.accepts === undefined
      ? STEP_UP_WAYS_IN
      : STEP_UP_WAYS_IN.filter((way) => read.accepts?.includes(way));
  return { ways, row: read.row ?? null };
}

/**
 * The ways in a refusal's CODE allows, or `null` when the gate does not answer it with a prompt
 * (see {@link stepUpRefusalAnswer}, which also says which row the code names).
 */
export function stepUpRefusalWays(
  error: unknown,
  refusalCodes: StepUpRefusalCodes = [],
): readonly StepUpWayIn[] | null {
  return stepUpRefusalAnswer(error, refusalCodes)?.ways ?? null;
}

/** Whether a caught failure is the backend asking the admin to re-confirm their identity. */
export function isStepUpRequired(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === STEP_UP_REQUIRED_ERROR_CODE;
}

/**
 * The gated action name the refusal names (`charity_payout_ach`, `delete_passkey`, …), or `null`
 * when absent.
 * Tolerant by contract — `context` values are `unknown` and the bag may be missing entirely, so a
 * caller uses this only to sharpen copy and must stay correct without it.
 */
export function stepUpActionOf(error: unknown): string | null {
  if (!isGivmoError(error)) return null;
  const action = firstErrorContext(error)?.action;
  return typeof action === 'string' && action.length > 0 ? action : null;
}

/**
 * Raised when the admin dismisses the verification prompt instead of completing it. A distinct type
 * because the two failures deserve opposite copy: a real submit failure tells them to check the
 * details they entered, while this one must say the details are untouched and still theirs to save —
 * telling someone to re-check a correct account number is how a good number gets "corrected" into a
 * wrong one.
 */
export class StepUpCancelledError extends Error {
  constructor() {
    super('Identity verification was cancelled.');
    this.name = 'StepUpCancelledError';
    // Prototype-chain restoration, matching `AuthError` — `instanceof` must survive down-levelling.
    Object.setPrototypeOf(this, StepUpCancelledError.prototype);
  }
}

/** Whether a caught failure is the admin's own dismissal of the verification prompt. */
export function isStepUpCancelled(error: unknown): boolean {
  return error instanceof StepUpCancelledError;
}

/**
 * The server's refusal a gate failed an act on because nothing here could answer it (the passkey-only
 * dead end, `StepUpPasskeyFailure('none_registered')`), when the failure carries one; `undefined`
 * otherwise. Its words are the server's own cure (a code-only account is told to set up a passkey),
 * so an act that reads its failure line off a `GivmoError` reads it off this too, never falling to
 * its generic line (AS-PWL-2e; the cut's review, F3).
 */
export function unansweredStepUpRefusal(thrown: unknown): GivmoError | undefined {
  if (!(thrown instanceof StepUpPasskeyFailure) || thrown.reason !== 'none_registered') {
    return undefined;
  }
  return isGivmoError(thrown.refusal) ? thrown.refusal : undefined;
}
